import React, { RefObject, useState, useEffect, useContext } from 'react';
import ProductsFlexGrid from '@tkww/reg-shared-ui-product-flexgrid';
import { useGetRegistryProducts } from '../../../hooks/useGetRegistryProducts';
import { PriceFilterPills, PRICE_RANGES } from '../PriceFilterPills';
import { Body1 } from '@xo-union/tk-ui-typography';
import { Button } from '@xo-union/tk-component-buttons';
import Icon from '@xo-union/tk-component-icons';
import { Bone, Skeleton } from '@xo-union/tk-ui-skeleton';
import { useShopByPriceStats } from '../../../hooks/useShopByPriceStats';
import { useWeddingWebsiteQuery } from '../../../generated/graphql';
import { getTargetOrigin } from '../../../helpers/getTargetOrigin';
import { TrackingContext } from '../../../TrackingContext';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import cx from 'classnames';
import styles from './styles.scss';

interface ShopByPriceContentProps {
  iframeRef?: RefObject<HTMLIFrameElement>;
  isPreview?: boolean;
  onItemAdded?: () => void;
}

const ShopByPriceContent: React.FC<ShopByPriceContentProps> = ({ iframeRef, isPreview = false, onItemAdded }) => {
  const { data } = useWeddingWebsiteQuery();
  const { track } = useContext(TrackingContext);
  const mobileMedia = useMobileMedia();
  const [selectedPrice, setSelectedPrice] = useState('50_99.99');

  const mapToShopLinkParam = (price: string): string => {
    const priceMap: Record<string, string> = {
      '*_49.99': '%2A_49.99',
      '50_99.99': '50.00_99.99',
      '100_149.99': '100.00_149.99',
      '150_*': '150.00_%2A',
    };
    return priceMap[price] || price;
  };

  const shopLinkParam = mapToShopLinkParam(selectedPrice);
  // Extract guestRange from wedding data
  const guestRange = (data?.wedding as any)?.guestRange;
  const { addedCount, recommendedMoreCount, isLoading: isLoadingStats, refetch } = useShopByPriceStats(
    shopLinkParam,
    guestRange
  );
  const { data: productsData, loading: isLoadingProducts } = useGetRegistryProducts('2', selectedPrice);
  const products = productsData?.category?.products?.edges?.map(({ node }: any) => node) || [];

  // Find the current price range label
  const selectedRange = PRICE_RANGES.find(range => range.value === selectedPrice);
  const rangeLabel = selectedRange?.label || '';

  useEffect(() => {
    const handleFocus = () => {
      refetch();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePriceChange = (price: string) => {
    setSelectedPrice(price);

    const selectedRange = PRICE_RANGES.find(range => range.value === price);

    track('Registry Interaction', {
      selection: selectedRange?.label || price,
      userDecisionArea: 'shop-by-price',
      source: 'registry eip',
      platform: mobileMedia.yes ? 'mobile' : 'desktop',
      product: 'wws',
    });
  };

  const handleSeeMore = () => {
    const baseUrl =
      process.env.REACT_APP_APP_ENV === 'production' ? 'https://www.theknot.com' : 'https://qa-beta.theknot.com';

    const url = `${baseUrl}/registry/store/popular-gifts?f_price=${shopLinkParam}`;

    window.open(url, '_blank', 'noopener,noreferrer');

    track('Registry Interaction', {
      selection: 'See more',
      userDecisionArea: 'shop-by-price',
      source: 'registry eip',
      platform: mobileMedia.yes ? 'mobile' : 'desktop',
      product: 'wws',
    });
  };

  const renderBlurb = () => {
    if (!guestRange) return <></>;

    if (isLoadingStats) {
      return (
        <Skeleton aria-label="loading gift statistics">
          <Bone style={{ height: 20, width: '100%', marginBottom: 4 }} />
          <Bone style={{ height: 20, width: '80%' }} />
        </Skeleton>
      );
    }

    const giftText = `${addedCount} gift${addedCount !== 1 ? 's' : ''}`;

    if (recommendedMoreCount > 0) {
      const recommendedText = `${recommendedMoreCount} more gift${recommendedMoreCount !== 1 ? 's' : ''}`;
      return (
        <div>
          You've added {giftText} in the <strong>{rangeLabel} price range</strong>. We recommend adding{' '}
          <strong>{recommendedText}</strong>.
        </div>
      );
    }

    return (
      <div>
        You've added {giftText} in the <strong>{rangeLabel} price range</strong>. Try adding gifts in other price ranges
        for more variety.
      </div>
    );
  };

  return (
    <div className={cx(styles.shopByPriceModule, { [styles.withPadding]: !isPreview })}>
      <div className={styles.header}>
        <Body1 bold className={styles.title}>
          Add popular gifts
        </Body1>
      </div>

      <div className={styles.guidanceCopy}>{renderBlurb()}</div>

      <div className={cx({ [styles['price-filter-pills-without-blurb']]: !guestRange })}>
        <PriceFilterPills selectedPrice={selectedPrice} onPriceChange={handlePriceChange} />
      </div>

      {products.length > 0 ? (
        <>
          <ProductsFlexGrid
            isLoading={isLoadingProducts}
            products={products as any}
            analyticsEventData={{
              filters: [],
              listId: 'shop-by-price',
              source: 'registry-eip',
            }}
            trackingConfiguration={{} as any}
            member={data?.member as any}
            onItemAddedToRegistry={() => {
              refetch();

              if (onItemAdded) {
                onItemAdded();
              }

              if (iframeRef?.current) {
                iframeRef.current.contentWindow?.postMessage(
                  JSON.stringify({
                    type: 'reload',
                  }),
                  getTargetOrigin()
                );
              }
            }}
          />
          <Button
            className={styles.seeMoreButton}
            color="secondary-alternative"
            size="lg"
            onClick={handleSeeMore}
            data-testid="shop-by-price-see-more"
            aria-label="See more popular gifts in this price range"
          >
            See more
            <Icon name="linkout" size="sm" />
          </Button>
        </>
      ) : (
        <div className={styles.emptyState} role="status" aria-live="polite">
          {isLoadingProducts ? 'Loading products...' : 'No products found in this price range'}
        </div>
      )}
    </div>
  );
};

export const ShopByPriceModule: React.FC<ShopByPriceContentProps> = ({ iframeRef, isPreview, onItemAdded }) => {
  return <ShopByPriceContent iframeRef={iframeRef} isPreview={isPreview} onItemAdded={onItemAdded} />;
};
